import React, { useRef, useState } from 'react';

interface FileUploadProps {
  onFileSelect: (file: File, disableAutoDetect: boolean) => void;
  disabled?: boolean;
  initialAutoDetect?: boolean;
}

const FileUpload: React.FC<FileUploadProps> = ({ onFileSelect, disabled = false, initialAutoDetect = true }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const [disableAutoDetect, setDisableAutoDetect] = useState(!initialAutoDetect);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.type === 'text/csv') {
      onFileSelect(file, disableAutoDetect);
    } else if (file) {
      alert('Please select a valid CSV file');
    }
  };

  const handleClick = () => {
    if (!disabled) {
      fileInputRef.current?.click();
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (!disabled) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    
    if (disabled) return;
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      if (file.type === 'text/csv') {
        onFileSelect(file, disableAutoDetect);
      } else {
        alert('Please select a valid CSV file');
      }
    }
  };

  return (
    <div className="w-full max-w-3xl mx-auto space-y-6">
      <div 
        className={`
          neon-drag-area p-16 text-center cursor-pointer transition-all duration-300
          ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
          ${isDragOver ? 'drag-over' : ''}
        `}
        onClick={handleClick}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div className="flex flex-col items-center space-y-8">
          {/* Neon style upload icon */}
          <div className={`
            w-32 h-32 relative transition-all duration-300
            ${disabled ? '' : isDragOver ? 'neon-glow-text' : ''}
          `}>
            <div className="neon-panel w-full h-full flex items-center justify-center">
              <div className="text-center">
                <div className="font-mono text-4xl text-neon-cyan mb-2">
                  ▲
                </div>
                <div className="font-mono text-lg text-neon-purple">
                  UPLOAD
                </div>
              </div>
            </div>
          </div>
          
          {/* Text content */}
          <div className="space-y-6 max-w-lg">
            <h3 className={`font-mono text-2xl uppercase transition-colors duration-300 ${
              disabled ? 'text-gray-500' : isDragOver ? 'neon-glow-text' : 'text-neon-pink'
            }`}>
              {isDragOver ? '> Drop CSV File Here <' : '> Upload CSV Data <'}
            </h3>
            <p className="font-mono text-lg text-gray-400 uppercase">
              Drag and Drop or Click to Browse
            </p>
            <div className="neon-panel p-4">
              <div className="font-mono text-sm space-y-2 text-center">
                <div className="text-neon-yellow">Max Size: 1GB</div>
                <div className="text-neon-blue">Format: .CSV files only</div>
                <div className={disabled ? 'text-red-400' : 'text-neon-green'}>
                  Status: {disabled ? '[DISABLED]' : '[READY]'}
                </div>
              </div>
            </div>
          </div>

          {/* Auto-detect toggle */}
          <div className="neon-panel p-4 flex items-center space-x-4">
            <label className="font-mono text-neon-green flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="form-checkbox h-5 w-5 text-neon-cyan focus:ring-neon-purple"
                checked={!disableAutoDetect}
                onChange={(e) => {
                  const isDisabled = !e.target.checked;
                  setDisableAutoDetect(isDisabled);
                  if (fileInputRef.current?.files?.[0]) {
                    onFileSelect(fileInputRef.current.files[0], isDisabled);
                  }
                }}
              />
              <span className="ml-2">Auto-detect column types</span>
            </label>
          </div>

          {/* Neon style action button */}
          {!disabled && (
            <button
              type="button"
              className="neon-button neon-button-blue text-lg px-8 py-4"
            >
              Select File
            </button>
          )}
        </div>
      </div>
      
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv"
        onChange={handleFileChange}
        className="hidden"
        disabled={disabled}
      />
    </div>
  );
};

export default FileUpload; 